import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { PersonSummaryModel } from 'src/app/models/person/person-summary.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { LoadingService } from 'src/app/services/loading.service';
import { TrainingGroupService } from 'src/app/services/training-group.service';

@Component({
  selector: 'app-training-group-persons',
  standalone: false,
  templateUrl: './training-group-persons.component.html',
  styleUrl: './training-group-persons.component.scss',
})
export class TrainingGroupPersonsComponent implements OnInit {
  @Input() trainingGroupId!: string;

  persons: PersonSummaryModel[] = [];
  personIds: string[] = [];

  personColumns: TableColumn[] = [];
  selectedPersons: PersonModel[] = [];

  showAddPersonModal: boolean = false;
  showDeletePersonModal: boolean = false;
  personToDeleteId: string | null = null;

  modalDeletePersonConfirmationOptions: ConfirmationDialogData | null = null;

  constructor(
    private trainingGroupService: TrainingGroupService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.personColumns = [
      {
        dataField: 'lastName',
        caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
        cssClass: 'width-200',
      },
      {
        dataField: 'firstName',
        caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
        cssClass: 'width-200',
      },
      {
        dataField: 'email',
        caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
        cssClass: 'width-150',
      },
      {
        dataField: 'location.name',
        caption: this.translateService.instant('COMMON.FIELDS.LOCATION'),
        cssClass: 'width-150',
      },
      {
        dataField: 'this',
        caption: '',
        cellType: 'action',
        disableFiltering: true,
        cellTemplate: 'actionsTemplate',
        actions: [
          {
            icon: 'close',
            class: 'red-icon',
            hint: this.translateService.instant('COMMON.DELETE'),
            isShowAction: () => true,
            onClick: (row: PersonSummaryModel, e: any) => {
              e.stopPropagation();
              e.preventDefault(); 
              e.stopImmediatePropagation();
              this.openDeletePersonConfirmationModal(row.id);
            }
          },
          {
            icon: 'link-tab',
            isShowAction: () => true,
            onClick: (person: PersonSummaryModel, e: any) => {
              e.stopPropagation();
              e.preventDefault();
              e.stopImmediatePropagation();
              this.navigateToEmployeeNewTab(person.id);
            },
          },
        ],
      },
    ];

    this.loadPersons();
  }

  private loadPersons(): void {
    this.loadingService
      .load(this.trainingGroupService.getTrainingGroupPersons(this.trainingGroupId))
      .subscribe((persons) => {
        this.persons = persons;
        this.personIds = persons.map(p => p.id);
      });
  }

  showAddEmployeesModal(): void {
    this.showAddPersonModal = true;
  }

  closeAddPersonModal = () => {
    this.showAddPersonModal = false;
    this.selectedPersons = [];
  };

  onAddPersons = () => {
    if (this.selectedPersons.length === 0) return;

    const selectedPersonIds = this.selectedPersons.map((p) => p.id);
    this.loadingService
      .load(this.trainingGroupService.addTrainingGroupPersons(this.trainingGroupId, selectedPersonIds))
      .subscribe(() => {
        this.closeAddPersonModal();
        this.loadPersons();
      });
  };

  openDeletePersonConfirmationModal(personId: string) {
    this.personToDeleteId = personId;
    this.modalDeletePersonConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.DELETE'),
      message: this.translateService.instant(
        'ADMINISTRATION.DELETE-PERSON-GROUP-CONFIRMATION-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.CANCEL'),
    };
    this.showDeletePersonModal = true;
  }

  deletePersonFromTrainingGroup(): void {
    if (!this.personToDeleteId) return;

    this.loadingService
      .load(this.trainingGroupService.deletePersonFromTrainingGroup(this.trainingGroupId, this.personToDeleteId))
      .subscribe(() => {
        this.showDeletePersonModal = false;
        this.personToDeleteId = null;
        this.loadPersons();
      });
  }

  navigateToEmployeeNewTab(id: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/employee/edit', id])
    );
    window.open(url, '_blank');
  }

  navigateToEmployee(id: string) {
    this.router.navigate(['/employee/edit', id]);
  }
}
